import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../contexts/PhpAuthContext';
import { useData } from '../contexts/DataContext';
import { Button } from '../components/ui/button';
import { toast } from '../components/ui/use-toast';
import { apiRequest } from '../config/api';
import { 
  LogOut,
  Stethoscope,
  BarChart3,
  FileText,
  Users,
  Calendar,
  Settings,
  ClipboardList,
  Clock
} from 'lucide-react';

import DoctorStatsCards from '../components/doctor/DoctorStatsCards';
import PatientSubmissionsList from '../components/doctor/PatientSubmissionsList';
import SubmissionDetailsAndFeedback from '../components/doctor/SubmissionDetailsAndFeedback';
import DoctorRecentActivity from '../components/doctor/DoctorRecentActivity';
import AdminPatientsTab from '../components/admin/AdminPatientsTab';
import AppointmentsTab from '../components/admin/AppointmentsTab';
import DoctorSettingsTab from '../components/doctor/DoctorSettingsTab';
import CampSubmissions from '../components/doctor/CampSubmissions';
import Attendance from '../components/doctor/Attendance';

const ClinicLoginDashboard = () => {
  const { profile, signOut } = useAuth();
  const { submissions, patients, doctors, updateSubmission, fetchSubmissions, fetchPatients, fetchDoctors } = useData();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [recommendations, setRecommendations] = useState('');
  const [nextAppointment, setNextAppointment] = useState('');
  const [submissionStatus, setSubmissionStatus] = useState('');
  const [appointmentsCount, setAppointmentsCount] = useState(0);
  const [submissionsCount, setSubmissionsCount] = useState(0);
  const [appointments, setAppointments] = useState([]);
  const [scheduledAppointmentsCount, setScheduledAppointmentsCount] = useState(0);
  const [campSubmissionsCount, setCampSubmissionsCount] = useState(0);
  const [attendanceCount, setAttendanceCount] = useState(0);
  const [campSubmissionsInitialTab, setCampSubmissionsInitialTab] = useState('form');
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [attendanceInitialTab, setAttendanceInitialTab] = useState('checkin');
  const [attendanceKey, setAttendanceKey] = useState(0);

  // Redirect if not clinic_login role
  useEffect(() => {
    if (profile && profile.role !== 'clinic_login') {
      navigate('/', { replace: true });
    }
  }, [profile, navigate]);

  // Refresh submissions after successful submission
  const refreshSubmissions = useCallback(() => {
    fetchSubmissions();
    fetchSubmissionsCount();
  }, [fetchSubmissions]);

  useEffect(() => {
    console.log('ClinicLoginDashboard useEffect: Profile:', profile);
    if (profile && profile.role === 'clinic_login') {
      console.log('ClinicLoginDashboard: Fetching data for clinic_login');
      fetchSubmissions();
      fetchPatients();
      fetchDoctors();
      fetchAppointmentsCount();
      fetchAppointments();
      fetchSubmissionsCount();
      fetchAttendanceCount();
    }
  }, [profile, fetchSubmissions, fetchPatients, fetchDoctors]);

  // Update current date/time every second (IST)
  useEffect(() => {
    const updateDateTime = () => {
      setCurrentDateTime(new Date());
    };
    
    updateDateTime(); // Initial update
    const timer = setInterval(updateDateTime, 1000);
    
    return () => clearInterval(timer);
  }, []);

  const fetchCampSubmissionsCount = useCallback(async () => {
    try {
      const url = 'get_camp_submissions.php?limit=1&offset=0';
      
      console.log('Fetching camp submissions count:', { url });
      
      const data = await apiRequest(url);
      
      console.log('Camp submissions count response:', data);
      
      if (data && data.success !== false) {
        const count = data.total || 0;
        console.log('Setting camp submissions count to:', count);
        setCampSubmissionsCount(count);
      } else {
        console.error('Failed to fetch camp submissions count:', data?.error || 'Unknown error');
        setCampSubmissionsCount(0);
      }
    } catch (error) {
      console.error('Error fetching camp submissions count:', error);
      setCampSubmissionsCount(0);
    }
  }, []);

  const fetchAttendanceCount = useCallback(async () => {
    try {
      const url = 'get_attendance.php?limit=1&offset=0';
      const data = await apiRequest(url);
      if (data.success) {
        setAttendanceCount(data.total || 0);
      } else {
        console.error('Failed to fetch attendance count:', data.error);
        setAttendanceCount(0);
      }
    } catch (error) {
      console.error('Error fetching attendance count:', error);
      setAttendanceCount(0);
    }
  }, []);

  // Fetch camp submissions count when profile changes
  useEffect(() => {
    if (profile && profile.role === 'clinic_login') {
      console.log('useEffect triggered - fetching camp submissions count');
      fetchCampSubmissionsCount();
      fetchAttendanceCount();
    }
  }, [profile, fetchCampSubmissionsCount, fetchAttendanceCount]);

  const fetchAppointmentsCount = async () => {
    try {
      const data = await apiRequest('get_appointments_count.php');
      if (data.success) {
        setAppointmentsCount(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching appointments count:', error);
      setAppointmentsCount(0);
    }
  };

  const fetchAppointments = async () => {
    try {
      // Clinic login users can only see their own appointments
      const url = `get_appointments_list.php?doctor_id=${profile?.id}`;
      const data = await apiRequest(url);
      if (data.success) {
        const appointmentsList = data.appointments || [];
        setAppointments(appointmentsList);
        // Count scheduled appointments
        const scheduled = appointmentsList.filter(apt => 
          (apt.status || '').toLowerCase() === 'scheduled'
        ).length;
        setScheduledAppointmentsCount(scheduled);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
      setAppointments([]);
      setScheduledAppointmentsCount(0);
    }
  };

  const fetchSubmissionsCount = async () => {
    try {
      const data = await apiRequest('get_submissions_count.php');
      if (data.success) {
        setSubmissionsCount(data.total || 0);
      } else {
        console.error('Failed to fetch submissions count:', data.error);
        setSubmissionsCount(0);
      }
    } catch (error) {
      console.error('Error fetching submissions count:', error);
      setSubmissionsCount(0);
    }
  };

  // Filter submissions - clinic_login can only see their own submissions
  const doctorSubmissions = React.useMemo(() => {
    return submissions.filter(sub => sub.doctor_id === profile?.id);
  }, [submissions, profile]);

  // Tabs for clinic login (same as doctor, conditionally show camp_submissions and attendance based on permissions)
  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'patients', label: 'Patients', icon: Users },
    { id: 'appointments', label: 'Appointments', icon: Calendar },
    { id: 'submissions', label: 'Appointment History', icon: FileText },
    // Conditionally add camp_submissions and attendance based on permissions
    ...(profile?.can_access_camp_submissions ? [{ id: 'camp_submissions', label: 'Camp Submissions', icon: ClipboardList }] : []),
    ...(profile?.can_access_attendance ? [{ id: 'attendance', label: 'Attendance', icon: Clock }] : []),
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  const handleTabChange = (tabId, subTab = null) => {
    // Handle attendance tab navigation first (before setting activeTab)
    if (tabId === 'attendance') {
      if (subTab) {
        // Set the specific sub-tab (e.g., 'approve_leave')
        setAttendanceInitialTab(subTab);
        // Force remount by updating key if already on attendance tab
        if (activeTab === 'attendance') {
          setAttendanceKey(prev => prev + 1);
        }
      } else if (activeTab === 'overview') {
        // Default to checkin when navigating from overview without subTab
        setAttendanceInitialTab('checkin');
      }
    } else if (tabId !== 'attendance') {
      // Reset to 'checkin' when navigating away from attendance
      setAttendanceInitialTab('checkin');
    }
    
    // If navigating to camp_submissions from overview (via count card), set initial tab to 'list'
    if (tabId === 'camp_submissions' && activeTab === 'overview') {
      setCampSubmissionsInitialTab('list');
    } else if (tabId !== 'camp_submissions') {
      // Reset to 'form' when navigating away
      setCampSubmissionsInitialTab('form');
    }
    setActiveTab(tabId);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
    toast({
      title: 'Logged out successfully',
      description: 'See you next time!'
    });
  };

  const handleSubmissionClick = (submission) => {
    setSelectedSubmission(submission);
    setFeedback(submission.doctor_feedback || '');
    setRecommendations(submission.recommendations || '');
    setNextAppointment(submission.next_appointment || '');
    setSubmissionStatus(submission.status || 'pending');
  };

  const handleUpdateSubmission = async () => {
    if (!selectedSubmission) return;

    const updates = {
      doctor_feedback: feedback,
      recommendations: recommendations,
      next_appointment: nextAppointment,
      status: submissionStatus,
      reviewed_at: new Date().toISOString(),
    };

    const { error } = await updateSubmission(selectedSubmission.id, updates);
    
    if (!error) {
      toast({
        title: 'Submission updated successfully',
        description: 'Patient has been notified of your feedback'
      });
      setSelectedSubmission(null);
    } else {
      toast({
        title: 'Error updating submission',
        variant: 'destructive'
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Clinic Login Dashboard - Gugan Dental Care</title>
        <meta name="description" content="Manage patients and appointments for your clinic" />
      </Helmet>
      
      <div className="min-h-screen">
        {/* Header */}
        <header className="glass-effect border-b border-gray-700">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
            <div className="flex justify-between items-center h-14 sm:h-16">
              <div className="flex items-center space-x-1 sm:space-x-2">
                <img 
                  src="/logo.png" 
                  alt="Gugan Dental Care Logo" 
                  className="h-8 sm:h-10 md:h-12 w-auto object-contain"
                  onError={(e) => {
                    const fallback = e.target.nextElementSibling;
                    if (fallback) {
                      e.target.style.display = 'none';
                      fallback.style.display = 'block';
                    }
                  }}
                />
                <Stethoscope className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400 hidden" />
                <span className="text-base sm:text-xl font-bold gradient-text">Gugan Dental Care</span>
              </div>
              
              <div className="flex items-center space-x-2 sm:space-x-4">
                <div className="hidden sm:flex flex-col items-end">
                  <span className="text-white text-xs sm:text-sm">Welcome, {profile?.full_name || profile?.name || 'Clinic Login'}</span>
                  {profile?.doctor_unique_id && (
                    <span className="text-gray-400 text-xs mt-0.5">Clinic Login ID: {profile.doctor_unique_id}</span>
                  )}
                </div>
                <span className="sm:hidden text-white text-xs truncate max-w-[120px]">{profile?.full_name || profile?.name || 'Clinic Login'}</span>
                <Button 
                  variant="ghost" 
                  onClick={handleLogout}
                  className="text-white hover:text-red-400 p-2 sm:px-3 sm:py-2"
                  size="sm"
                >
                  <LogOut className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Logout</span>
                </Button>
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
          {/* Welcome Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-4 sm:mb-6 md:mb-8"
          >
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold gradient-text mb-1 sm:mb-2">
                  Clinic Login Dashboard
                </h1>
                <p className="text-gray-300 text-sm sm:text-base md:text-lg">
                  Manage patients, review submissions, and provide medical feedback
                </p>
              </div>
              <div className="text-right">
                <div className="p-3 bg-gray-800 rounded-lg border border-gray-700">
                  <p className="text-gray-400 text-xs mb-1">Current Date & Time (IST)</p>
                  <p className="text-white font-semibold text-sm sm:text-base">
                    {currentDateTime.toLocaleDateString("en-IN", {
                      weekday: "short",
                      year: "numeric",
                      month: "short",
                      day: "2-digit",
                      timeZone: "Asia/Kolkata",
                    })}
                  </p>
                  <p className="text-blue-400 font-mono text-lg sm:text-xl font-bold">
                    {currentDateTime.toLocaleTimeString("en-IN", {
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                      hour12: true,
                      timeZone: "Asia/Kolkata",
                    })}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Navigation Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-4 sm:mb-6 md:mb-8"
          >
            <div className="flex space-x-1 bg-gray-800 p-1 rounded-lg overflow-x-auto scrollbar-hide">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-md transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  <tab.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="text-xs sm:text-sm">{tab.label}</span>
                </button>
              ))}
            </div>
          </motion.div>

          {/* Tab Content */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            {activeTab === 'overview' && (
              <>
                {/* Stats Cards */}
                <DoctorStatsCards 
                  doctorSubmissions={doctorSubmissions} 
                  patients={patients} 
                  appointmentsCount={appointmentsCount}
                  scheduledAppointmentsCount={scheduledAppointmentsCount}
                  submissionsCount={submissionsCount}
                  campSubmissionsCount={campSubmissionsCount}
                  attendanceCount={attendanceCount}
                  onNavigate={handleTabChange}
                  canAccessCampSubmissions={profile?.can_access_camp_submissions || false}
                  canAccessAttendance={profile?.can_access_attendance || false}
                />

                <div className="flex flex-col lg:flex-row gap-8 mt-8">
                  {/* Patient Submissions - 65% width */}
                  <div className="w-full lg:w-[65%]">
                    <PatientSubmissionsList 
                      doctorSubmissions={doctorSubmissions} 
                      handleSubmissionClick={handleSubmissionClick} 
                      selectedSubmission={selectedSubmission}
                      onRefresh={refreshSubmissions}
                    />
                  </div>

                  {/* Submission Details & Feedback / Doctor Profile - 35% width */}
                  <div className="w-full lg:w-[35%]">
                    <SubmissionDetailsAndFeedback
                      selectedSubmission={selectedSubmission}
                      feedback={feedback}
                      setFeedback={setFeedback}
                      recommendations={recommendations}
                      setRecommendations={setRecommendations}
                      nextAppointment={nextAppointment}
                      setNextAppointment={setNextAppointment}
                      submissionStatus={submissionStatus}
                      setSubmissionStatus={setSubmissionStatus}
                      handleUpdateSubmission={handleUpdateSubmission}
                      setSelectedSubmission={setSelectedSubmission}
                      profile={profile}
                    />
                  </div>
                </div>

                {/* Recent Activity */}
                <DoctorRecentActivity 
                  submissions={doctorSubmissions}
                  appointments={appointments}
                  profile={profile}
                />
              </>
            )}
            {activeTab === 'submissions' && (
              <PatientSubmissionsList 
                doctorSubmissions={doctorSubmissions} 
                handleSubmissionClick={handleSubmissionClick} 
                selectedSubmission={selectedSubmission}
                onRefresh={refreshSubmissions}
              />
            )}
            {activeTab === 'patients' && <AdminPatientsTab onSubmissionComplete={refreshSubmissions} viewAsAdmin={false} />}
            {activeTab === 'appointments' && <AppointmentsTab viewAsAdmin={false} />}
            {activeTab === 'camp_submissions' && <CampSubmissions initialTab={campSubmissionsInitialTab} viewAsAdmin={false} />}
            {activeTab === 'attendance' && <Attendance key={`attendance-${attendanceInitialTab}-${attendanceKey}`} initialTab={attendanceInitialTab} viewAsAdmin={false} isAdmin={false} />}
            {activeTab === 'settings' && <DoctorSettingsTab />}
          </motion.div>
        </div>

        {/* Footer */}
        <footer className="border-t border-gray-700 py-8 px-4 sm:px-6 lg:px-8 mt-8">
          <div className="max-w-7xl mx-auto text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <img 
                src="/logo.png" 
                alt="Gugan Dental Care Logo" 
                className="h-8 w-auto object-contain"
                onError={(e) => {
                  const fallback = e.target.nextElementSibling;
                  if (fallback) {
                    e.target.style.display = 'none';
                    fallback.style.display = 'block';
                  }
                }}
              />
              <Stethoscope className="h-6 w-6 text-blue-400 hidden" />
              <span className="text-lg font-semibold gradient-text">Gugan Dental Care</span>
            </div>
            <p className="text-gray-400 text-sm">
              © 2025 Gugan Dental Care. All rights reserved. Developed and maintained by{' '}
              <a 
                href="https://www.neinus.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline transition-colors"
              >
                Neinus Technologies
              </a>
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ClinicLoginDashboard;
